import { canEditInvoices, canSeePath } from './lib/area';
import type { Me } from '@ft/shared';
import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { fetchMe, loginUrl, type MeResult } from './api';
import { AppShell } from './components/AppShell';
import { useI18n } from './i18n';
import { initArea } from './lib/area';
import { CollapseProvider } from './lib/collapse';
import { AdminPage } from './pages/AdminPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { ForecastPage } from './pages/ForecastPage';
import { ReportPage } from './pages/ReportPage';
import { MarginPage } from './pages/MarginPage';
import { ProjectAnalysisPage } from './pages/ProjectAnalysisPage';
import { IssuesPage } from './pages/IssuesPage';
import { OrderPage } from './pages/OrderPage';
import { OrdersPage } from './pages/OrdersPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ContractsPage } from './pages/ContractsPage';
import { ContractPage } from './pages/ContractPage';
import { Placeholder } from './pages/Placeholder';
import { StartPage } from './pages/StartPage';
import { WorkdayPage } from './pages/WorkdayPage';
import { HelpPage } from './pages/HelpPage';

export function App() {
  const { t } = useI18n();
  const [state, setState] = useState<MeResult | null>(null);
  useEffect(() => { void fetchMe().then(setState); }, []);

  if (!state) return <div className="center muted">{t('app.loading')}</div>;
  if (state.kind === 'login') {
    location.href = loginUrl();
    return <div className="center muted">{t('app.toLogin')}</div>;
  }
  if (state.kind === 'not_registered') {
    return (
      <div className="center">
        <div className="card">
          <h1>{t('app.noAccess.title')}</h1>
          <p>{t('app.noAccess.text')}</p>
          <p className="muted">{t('app.signedInAs', { email: state.email })}</p>
          <a className="btn" href="/auth/logout">{t('app.logout')}</a>
        </div>
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="center">
        <div className="card">
          <h1>{t('app.down.title')}</h1>
          <p>{t('app.down.text', { reason: state.status ? t('app.serverStatus', { status: state.status }) : t('app.serverDown') })}</p>
          <button className="btn primary" onClick={() => location.reload()}>{t('app.reload')}</button>
        </div>
      </div>
    );
  }

  const me = state.me;
  const isAdmin = me.role === 'admin';
  initArea(me.areas);
  // Ohne Bereich sieht man keine Daten; nur Admins kommen noch in die Verwaltung
  if (!me.areas.length && !isAdmin) {
    return (
      <div className="center">
        <div className="card">
          <h1>{t('app.noArea.title')}</h1>
          <p>{t('app.noArea.text')}</p>
          <p className="muted">{t('app.signedInAs', { email: me.email })}</p>
          <a className="btn" href="/auth/logout">{t('app.logout')}</a>
        </div>
      </div>
    );
  }
  return (
    <CollapseProvider initial={me.collapsed ?? []}>
    <BrowserRouter>
      <AppShell me={me}>
        <Routes>
          <Route path="/" element={<StartPage me={me} />} />
          <Route path="/bestellungen" element={<Allowed me={me} path="/bestellungen"><OrdersPage me={me} /></Allowed>} />
          <Route path="/bestellungen/:id" element={<Allowed me={me} path="/bestellungen"><OrderPage me={me} /></Allowed>} />
          <Route path="/vertraege" element={<Allowed me={me} path="/vertraege"><ContractsPage me={me} /></Allowed>} />
          <Route path="/vertraege/:id" element={<Allowed me={me} path="/vertraege"><ContractPage me={me} /></Allowed>} />
          <Route path="/offene-punkte" element={<Allowed me={me} path="/offene-punkte"><IssuesPage me={me} /></Allowed>} />
          <Route path="/projekte" element={<Allowed me={me} path="/projekte"><ProjectsPage me={me} /></Allowed>} />
          <Route path="/invoicing" element={<Allowed me={me} path="/invoicing"><InvoicesPage me={me} /></Allowed>} />
          <Route path="/forecast" element={<Allowed me={me} path="/forecast"><ForecastPage me={me} /></Allowed>} />
          <Route path="/report" element={<ReportPage me={me} />} />
          {me.role !== 'reader' && <Route path="/marge" element={<MarginPage me={me} />} />}
          {me.role !== 'reader' && <Route path="/marge/:wd" element={<ProjectAnalysisPage me={me} />} />}
          <Route path="/hilfe" element={<HelpPage />} />
          {canEditInvoices(me) && <Route path="/invoicing/workday" element={<WorkdayPage />} />}
          {isAdmin && <Route path="/verwaltung" element={<Navigate to="/verwaltung/personen" replace />} />}
          {isAdmin && <Route path="/verwaltung/:tab" element={<AdminPage me={me} />} />}
          {/* alte Pfade */}
          {isAdmin && <Route path="/import" element={<Navigate to="/verwaltung/import" replace />} />}
          {isAdmin && <Route path="/zustaendigkeiten" element={<Navigate to="/verwaltung/projekte" replace />} />}
          <Route path="*" element={<Placeholder title={t('common.notFound.title')} />} />
        </Routes>
      </AppShell>
    </BrowserRouter>
    </CollapseProvider>
  );
}

/** Seite nur zeigen, wenn sie für die Person freigeschaltet ist (Rolle „lesend“ kann eingeschränkt sein). */
function Allowed({ me, path, children }: { me: Me; path: string; children: React.ReactNode }) {
  const { t } = useI18n();
  if (canSeePath(me, path)) return <>{children}</>;
  return (
    <section className="panel" style={{ marginTop: 24 }}>
      <h2>{t('sections.forbidden.title')}</h2>
      <p className="muted" style={{ margin: 0 }}>{t('sections.forbidden.text')}</p>
    </section>
  );
}
